import {
  Alert,
  Button,
  Checkbox,
  HelperText,
  TextInput,
  type FormControl,
} from 'platform/components';
import {Box, Grid, HStack, Image, Show, Text, VStack} from 'platform/foundation';

import {useState} from 'react';
import {useController} from 'react-hook-form';

import i18n from '@dms/i18n';
import {platformImageURLs} from '@dms/shared';

import {getSafely} from 'shared/web';

import type {BulkOperationActionCallbackParams} from 'features/eag-grid';

type PlatformParameter = NonNullable<
  BulkOperationActionCallbackParams['paramsSchema']
>['root']['elements'][number];

type VehicleAdvertisingPlatformsFieldProps = {
  control: FormControl<{platforms: string[]}>;
  label: string;
  options: NonNullable<PlatformParameter['defaultOptions']>;
};

export function VehicleAdvertisingPlatformsField(props: VehicleAdvertisingPlatformsFieldProps) {
  const [search, setSearch] = useState('');
  const {field, fieldState, formState} = useController({control: props.control, name: 'platforms'});
  const enabledPlatforms = props.options
    .filter((option) => !option.isDisabled)
    .map((option) => option.value);
  const selectedPlatforms = new Set(field.value);
  const isAllSelected =
    enabledPlatforms.length > 0 && enabledPlatforms.every((value) => selectedPlatforms.has(value));
  const isDisabled = field.disabled || formState.isSubmitting;
  const normalizedSearch = search.trim().toLocaleLowerCase(i18n.resolvedLanguage);
  const filteredOptions = props.options.filter((option) =>
    option.label.toLocaleLowerCase(i18n.resolvedLanguage).includes(normalizedSearch)
  );

  return (
    <section aria-label={props.label}>
      <Box padding={5} backgroundColor="palettes.neutral.10.100" borderRadius="medium">
        <VStack spacing={4}>
          <HStack spacing={3} align="center" justify="space-between" wrap>
            <VStack spacing={1}>
              <Text size="small">
                <strong>{props.label}</strong>
              </Text>
              <Text size="xSmall" color="secondary">
                <output aria-live="polite">
                  {i18n.t('general.labels.selected')}: {field.value.length} /{' '}
                  {enabledPlatforms.length}
                </output>
              </Text>
            </VStack>
            <Button
              title={i18n.t(
                isAllSelected ? 'general.actions.unselectAll' : 'general.actions.selectAll'
              )}
              variant="link"
              size="small"
              isDisabled={isDisabled || enabledPlatforms.length === 0}
              onClick={() => field.onChange(isAllSelected ? [] : enabledPlatforms)}
            />
          </HStack>
          <Show when={enabledPlatforms.length > 0}>
            <TextInput
              label={i18n.t('general.labels.search')}
              value={search}
              onChange={(value) => setSearch(value ?? '')}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                }
              }}
              isDisabled={isDisabled}
            />
            <Box maxHeight={80} overflowY="auto">
              <Grid columns={[1, 2, 3]} spacing={3}>
                {filteredOptions.map((option) => (
                  <Box
                    key={option.value}
                    padding={4}
                    borderRadius="medium"
                    border="thin solid"
                    borderColor={
                      selectedPlatforms.has(option.value) ? 'general.accent' : 'general.separator'
                    }
                    opacity={option.isDisabled ? 0.5 : 1}
                    backgroundColor={
                      selectedPlatforms.has(option.value) ? 'palettes.blue.10.100' : 'general.white'
                    }
                  >
                    <HStack spacing={3} align="center" justify="space-between">
                      <Checkbox
                        label={option.label}
                        name={field.name}
                        value={selectedPlatforms.has(option.value)}
                        isDisabled={isDisabled || option.isDisabled}
                        isInvalid={fieldState.invalid}
                        onBlur={field.onBlur}
                        onChange={(isChecked) =>
                          field.onChange(
                            isChecked
                              ? [...field.value, option.value]
                              : field.value.filter((value) => value !== option.value)
                          )
                        }
                      />
                      <Show
                        when={getSafely(platformImageURLs, option.value)}
                        onTablet
                        onNotebook
                        onDesktop
                      >
                        {(src) => <Image src={src} alt="" width={18} height={5} fit="contain" />}
                      </Show>
                    </HStack>
                  </Box>
                ))}
              </Grid>
            </Box>
            <Show when={filteredOptions.length === 0}>
              <Text color="secondary">{i18n.t('general.labels.noData')}</Text>
            </Show>
          </Show>
          <Show when={enabledPlatforms.length === 0}>
            <Alert
              variant="info"
              message={i18n.t('page.advertisement.labels.notAvailablePlatforms')}
            />
          </Show>
          <Show when={fieldState.error?.message}>
            {(message) => <HelperText errorMessage={message} />}
          </Show>
        </VStack>
      </Box>
    </section>
  );
}
